/** Local AI drafting — v2-FIRST feature (2026-07-25).
 *
 * Draws outreach copy for a lead from a model running on Tom's OWN Mac
 * (LM Studio's OpenAI-compatible server, default http://localhost:1234). The
 * browser talks to localhost directly: no server change, no API key, no
 * third-party call, nothing about a seller ever leaves the machine. If the
 * local server isn't running the panel says so plainly and offers no buttons —
 * same honest-empty-state rule the Deal Inbox uses for its missing AI key.
 *
 * Why the guardrails live HERE and not in the prompt: measured on Tom's M4 Pro
 * 2026-07-25, asking a local model to police its own output ("never reuse a
 * phrase", exact word counts) sends reasoning models into a 24,000-character
 * self-audit before they answer. Enforcing length, links, phone numbers and
 * near-duplicates in code is both faster and actually reliable. The prompt asks
 * for copy; this file decides what is safe to show.
 *
 * Facebook rules encoded (Tom's playbook, after his 2026-07-25 24h block):
 * comments and first DMs carry NO links and NO phone numbers, nothing is
 * posted automatically, and near-identical variations are flagged so two of
 * them never go out. Every draft is generated FROM the lead record, so the
 * property-specific detail that keeps a comment human is real, not invented.
 *
 * Writes nothing: no ops, no Supabase, no outbox. Text in, text out. */

export interface LocalAiConfig {
  base: string;
  model: string;
}

export interface LocalAiStatus {
  state: "unknown" | "off" | "on";
  runtime: string;
  models: string[];
  detail: string;
}

export type CopyKind = "comment" | "dm" | "group_post" | "listing";

export interface CopyKindSpec {
  label: string;
  /** what we ask the model for */
  what: string;
  min: number;
  max: number;
  /** comments and first messages must never carry a link or a phone number */
  stripContact: boolean;
}

export interface Draft {
  text: string;
  words: number;
  flags: string[];
}

export const LOCALAI_STORE = "crm2_localai";
export const DEFAULT_BASE = "http://localhost:1234";
/** probed in order when nothing is configured: LM Studio, then Ollama */
export const KNOWN_BASES: [string, string][] = [
  ["LM Studio", "http://localhost:1234"],
  ["Ollama", "http://localhost:11434"],
];
/** models we prefer when several are loaded */
const MODEL_RANK = ["qwen3.6", "qwen3.5", "qwen", "gemma", "gpt-oss", "llama", "mistral"];

export const COPY_KINDS: Record<CopyKind, CopyKindSpec> = {
  comment: {
    label: "Comment",
    what: "a short comment to leave on someone's Facebook post about this property, asking them to send the details",
    min: 12, max: 38, stripContact: true,
  },
  dm: {
    label: "First DM",
    what: "a first Facebook message to the person who posted this property — reference their post in the first line, ask one clear question",
    min: 30, max: 85, stripContact: true,
  },
  group_post: {
    label: "Group post",
    what: "a Facebook group post looking to buy a property like this one",
    min: 40, max: 120, stripContact: false,
  },
  listing: {
    label: "Marketplace",
    what: "a Facebook Marketplace listing description for this property, leading with its strongest feature",
    min: 70, max: 160, stripContact: false,
  },
};

/* ------------------------------------------------------------------ config */

/** Device-local, like v2's saved views — the endpoint is a per-machine fact. */
export function loadConfig(): LocalAiConfig {
  try {
    const raw = localStorage.getItem(LOCALAI_STORE);
    if (raw) {
      const p = JSON.parse(raw) as Partial<LocalAiConfig>;
      return {
        base: typeof p.base === "string" && p.base.trim() ? normalizeBase(p.base) : DEFAULT_BASE,
        model: typeof p.model === "string" ? p.model : "",
      };
    }
  } catch { /* unreadable storage — fall through to defaults */ }
  return { base: DEFAULT_BASE, model: "" };
}

export function saveConfig(cfg: LocalAiConfig): void {
  try { localStorage.setItem(LOCALAI_STORE, JSON.stringify(cfg)); } catch { /* private mode */ }
}

/** "http://localhost:1234/v1/" and "localhost:1234" both mean the same server. */
export function normalizeBase(input: string): string {
  let s = input.trim().replace(/\/+$/, "");
  if (!s) return DEFAULT_BASE;
  if (!/^https?:\/\//i.test(s)) s = "http://" + s;
  return s.replace(/\/v1$/i, "");
}

export function pickModel(ids: string[], preferred?: string): string {
  if (preferred && ids.includes(preferred)) return preferred;
  for (const want of MODEL_RANK) {
    const hit = ids.find((id) => id.toLowerCase().includes(want));
    if (hit) return hit;
  }
  return ids[0] ?? "";
}

/* --------------------------------------------------------------- discovery */

interface ModelsResponse { data?: { id?: string }[] }

/** Probe one base URL. Returns [] when nothing answers — never throws. */
export async function probe(base: string, fetchImpl: typeof fetch = fetch): Promise<string[]> {
  try {
    const res = await fetchImpl(normalizeBase(base) + "/v1/models");
    if (!res.ok) return [];
    const body = (await res.json()) as ModelsResponse;
    return (body.data ?? []).map((m) => m.id ?? "").filter(Boolean);
  } catch {
    return [];
  }
}

/** Try the configured base first, then the known ones. */
export async function discover(cfg: LocalAiConfig, fetchImpl: typeof fetch = fetch): Promise<LocalAiStatus> {
  const tried: [string, string][] = [["Local AI", cfg.base], ...KNOWN_BASES.filter(([, b]) => normalizeBase(b) !== normalizeBase(cfg.base))];
  for (const [runtime, base] of tried) {
    const models = await probe(base, fetchImpl);
    if (models.length) {
      const chat = models.filter((m) => !/embed/i.test(m));
      return { state: "on", runtime, models: chat.length ? chat : models, detail: normalizeBase(base) };
    }
  }
  return { state: "off", runtime: "", models: [], detail: "" };
}

/* ------------------------------------------------------- prompt + context */

const s = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
const money = (n: number | null): string => (n === null ? "" : "$" + Math.round(n).toLocaleString("en-US"));

/** City / area out of "123 Main St, Ocala, FL 34470" (v1 addresses are comma
 * separated; the segment before the state is the city). */
export function cityOf(address: string): string {
  const parts = address.split(",").map((p) => p.trim()).filter(Boolean);
  const second = parts[1] ?? "";
  if (parts.length >= 3) return second;
  if (parts.length === 2) return /^[A-Z]{2}\b/.test(second) ? "" : second;
  return "";
}

export interface LeadLike {
  address: string;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  price: number | null;
  arv: number | null;
  yearBuilt: number | null;
  cdom: number | null;
  desc: string | null;
  county: string | null;
}

/** The whole point of drafting inside the CRM: the specific detail that makes a
 * comment read as human is already on the lead. Nothing here is invented — every
 * line is a field that exists, and missing fields are simply left out. */
export function leadContext(lead: LeadLike, fields: Record<string, unknown> | undefined): string {
  const f = fields ?? {};
  const city = cityOf(lead.address);
  const out: string[] = [];
  out.push("Address: " + lead.address);
  if (city) out.push("City/area: " + city);
  else if (lead.county) out.push("County: " + lead.county);
  if (lead.beds !== null || lead.baths !== null) out.push(`Beds/baths: ${lead.beds ?? "?"}/${lead.baths ?? "?"}`);
  if (lead.sqft) out.push("Sqft: " + lead.sqft.toLocaleString("en-US"));
  if (lead.yearBuilt) out.push("Year built: " + lead.yearBuilt);
  if (lead.price !== null) out.push("Asking: " + money(lead.price));
  if (lead.arv !== null) out.push("ARV estimate: " + money(lead.arv));
  if (lead.cdom !== null) out.push("Days on market: " + lead.cdom);
  const cond = s(f["condition"]) || s(f["whhCond"]);
  if (cond) out.push("Condition: " + cond);
  const roof = s(f["roofCond"]) || s(f["roofAge"]);
  if (roof) out.push("Roof: " + roof);
  const occ = s(f["occupancy"]);
  if (occ) out.push("Occupancy: " + occ);
  const notes = s(f["notes"]);
  if (notes) out.push("Notes: " + notes.slice(0, 400));
  const desc = s(lead.desc);
  if (desc) out.push("Listing description: " + desc.slice(0, 600));
  return out.join("\n");
}

export interface ChatMessage { role: "system" | "user"; content: string }

/** Deliberately light on rules — the mechanical constraints are enforced in
 * code below, because asking a reasoning model to self-police costs a minute
 * of thinking per batch (measured 2026-07-25). */
export function buildMessages(kind: CopyKind, context: string, count: number): ChatMessage[] {
  const spec = COPY_KINDS[kind];
  const system =
    "You write for Tom at ETO Property Group, who buys houses to flip and hold as rentals within about " +
    "3 hours of Orlando. Plain Florida-local voice, contractions, no corporate fluff, no emojis, no ALL-CAPS." +
    (spec.stripContact ? " Never include links or phone numbers." : "") +
    " Output only a numbered list, nothing before or after it.";
  const user =
    `Write ${count} different versions of ${spec.what}.\n` +
    `${spec.min}-${spec.max} words each. Vary how each one opens and closes.\n` +
    `Work in the real details below — never invent facts that aren't here.\n\n${context}`;
  return [
    { role: "system", content: system },
    { role: "user", content: user },
  ];
}

/* ------------------------------------------------- parsing and guardrails */

/** Reasoning models emit <think> blocks inline; LM Studio also returns a
 * separate reasoning_content field, which we simply never read. */
export function stripThinking(text: string): string {
  return text.replace(/<think>[\s\S]*?<\/think>/gi, "").replace(/<\/?think>/gi, "").trim();
}

export function parseVariations(text: string): string[] {
  const clean = stripThinking(text);
  const numbered = clean.split(/^\s*\d+[.)]\s+/m).map((p) => p.trim()).filter(Boolean);
  if (numbered.length > 1) return numbered;
  return clean.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
}

const URL_RE = /(https?:\/\/\S+|www\.\S+|\b\S+\.(?:com|net|org|io|ai)\b\S*)/gi;
const PHONE_RE = /(\+?1?[\s\-.(]*\d{3}[)\s\-.]*\d{3}[\s\-.]*\d{4})/g;

/** 4-word shingle overlap — two drafts that share more than a third of their
 * phrasing are the same comment wearing a hat, and posting both is the exact
 * duplicate-content pattern that got the account blocked. */
export function similarity(a: string, b: string): number {
  const shingles = (t: string): Set<string> => {
    const w = t.toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/).filter(Boolean);
    const g = new Set<string>();
    for (let i = 0; i + 4 <= w.length; i++) g.add(w.slice(i, i + 4).join(" "));
    return g;
  };
  const A = shingles(a); const B = shingles(b);
  if (!A.size || !B.size) return 0;
  let hit = 0;
  A.forEach((x) => { if (B.has(x)) hit++; });
  return hit / Math.min(A.size, B.size);
}

export const DUPLICATE_AT = 0.35;

/** Strip what must never be posted, count what must be counted, flag the rest. */
export function guard(raw: string, kind: CopyKind): Draft {
  const spec = COPY_KINDS[kind];
  const flags: string[] = [];
  let text = raw.replace(/^["'“”]+|["'“”]+$/g, "").trim();
  if (spec.stripContact) {
    if (URL_RE.test(text)) { text = text.replace(URL_RE, ""); flags.push("link removed"); }
    URL_RE.lastIndex = 0;
    if (PHONE_RE.test(text)) { text = text.replace(PHONE_RE, ""); flags.push("phone number removed"); }
    PHONE_RE.lastIndex = 0;
  }
  text = text.replace(/\s{2,}/g, " ").trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  if (words > spec.max) flags.push(`${words} words — trim to ${spec.max}`);
  if (words && words < spec.min) flags.push(`only ${words} words — thin`);
  return { text, words, flags };
}

/** Guard every variation, then flag pairs that are too close to each other. */
export function toDrafts(variations: string[], kind: CopyKind): Draft[] {
  const drafts = variations.map((v) => guard(v, kind)).filter((d) => d.text.length > 0);
  drafts.forEach((d, i) => {
    for (let j = 0; j < i; j++) {
      const prev = drafts[j];
      if (prev && similarity(d.text, prev.text) > DUPLICATE_AT) { d.flags.push(`too similar to #${j + 1} — use one`); break; }
    }
  });
  return drafts;
}

/* ----------------------------------------------------------- the call */

interface ChatResponse {
  choices?: { message?: { content?: string | null }; finish_reason?: string }[];
}

/** Generous by necessity: local reasoning models spend most of their budget
 * thinking before the first visible word, and an 800-token cap comes back
 * EMPTY with finish_reason "length" (measured on both Qwen3.6 and Gemma 4,
 * 2026-07-25). Neither reasoning_effort nor chat_template_kwargs turns that
 * off through LM Studio's API — only its own settings do. */
export const MAX_TOKENS = 12000;

export class LocalAiError extends Error {}

/** Raw chat call — used by triage, which wants structured JSON back rather than
 * marketing copy. Same empty-answer handling as generate(): a reasoning model
 * that spends its whole budget thinking gets explained, not swallowed. */
export async function chat(
  cfg: LocalAiConfig,
  messages: ChatMessage[],
  temperature = 0.2,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const res = await fetchImpl(normalizeBase(cfg.base) + "/v1/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: cfg.model, messages, temperature, max_tokens: MAX_TOKENS, stream: false }),
  });
  if (!res.ok) throw new LocalAiError(`The local model answered ${res.status}. Is LM Studio still running?`);
  const body = (await res.json()) as ChatResponse;
  const choice = body.choices?.[0];
  const content = choice?.message?.content ?? "";
  if (!content.trim()) {
    throw new LocalAiError(
      choice?.finish_reason === "length"
        ? "The model spent its whole budget thinking and never answered. Turn the reasoning setting down in LM Studio, or triage fewer posts at once."
        : "The model returned nothing. Try again.",
    );
  }
  return stripThinking(content);
}

export async function generate(
  cfg: LocalAiConfig,
  kind: CopyKind,
  context: string,
  count: number,
  fetchImpl: typeof fetch = fetch,
): Promise<Draft[]> {
  const res = await fetchImpl(normalizeBase(cfg.base) + "/v1/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: cfg.model,
      messages: buildMessages(kind, context, count),
      temperature: 0.9,
      max_tokens: MAX_TOKENS,
      stream: false,
    }),
  });
  if (!res.ok) throw new LocalAiError(`The local model answered ${res.status}. Is LM Studio still running?`);
  const body = (await res.json()) as ChatResponse;
  const choice = body.choices?.[0];
  const content = choice?.message?.content ?? "";
  if (!content.trim()) {
    throw new LocalAiError(
      choice?.finish_reason === "length"
        ? "The model spent its whole budget thinking and never wrote the drafts. Turn the thinking/reasoning setting down in LM Studio, or load a smaller model."
        : "The model returned nothing. Try Draft again.",
    );
  }
  return toDrafts(parseVariations(content), kind);
}
